import React, { useState, useEffect, useCallback } from 'react';
import './GameEngine.css';

const GameEngine = ({ gameType, onClose, addOutput }) => {
  const [gameState, setGameState] = useState(null);
  const [isActive, setIsActive] = useState(false);

  const initializeGame = useCallback(() => {
    switch(gameType) {
      case 'snake':
        setGameState({
          snake: [{x: 10, y: 10}],
          food: {x: 15, y: 15, algorithm: 'PyTorch'},
          direction: 'right',
          score: 0,
          gameOver: false,
          algorithms: ['PyTorch', 'TensorFlow', 'Transformers', 'BERT', 'GPT', 'LSTM', 'CNN', 'RNN']
        });
        break;
      case 'pong':
        setGameState({
          playerY: 10,
          aiY: 10,
          ballX: 20,
          ballY: 10,
          ballVelX: 1,
          ballVelY: 1,
          playerScore: 0,
          aiScore: 0,
          gameOver: false
        });
        break;
      case 'typing':
        const codeSnippets = [
          'import torch.nn as nn',
          'model = torch.load("model.pth")',
          'optimizer = torch.optim.Adam(params)',
          'loss_fn = nn.CrossEntropyLoss()',
          'for epoch in range(100):'
        ];
        setGameState({
          targetText: codeSnippets[Math.floor(Math.random() * codeSnippets.length)],
          userInput: '',
          startTime: null,
          wpm: 0,
          accuracy: 100,
          gameOver: false
        });
        break;
      case 'memory':
        const sequences = [
          ['Linear', 'ReLU', 'Dropout', 'Linear', 'Softmax'],
          ['Conv2d', 'BatchNorm', 'ReLU', 'MaxPool', 'Flatten'],
          ['Embedding', 'LSTM', 'Dense', 'Sigmoid'],
          ['Attention', 'LayerNorm', 'FFN', 'Dropout']
        ];
        const sequence = sequences[Math.floor(Math.random() * sequences.length)];
        setGameState({
          sequence,
          userSequence: [],
          showSequence: true,
          phase: 'memorize',
          score: 0,
          gameOver: false
        });
        break;
    }
  }, [gameType]);

  useEffect(() => {
    initializeGame();
    setIsActive(true);
  }, [initializeGame]);

  // Snake Game Logic
  const moveSnake = useCallback((direction) => {
    if (!isActive || gameState?.gameOver) return;
    
    setGameState(prev => {
      const newSnake = [...prev.snake];
      const head = {...newSnake[0]};
      
      switch(direction) {
        case 'up': head.y -= 1; break;
        case 'down': head.y += 1; break;
        case 'left': head.x -= 1; break;
        case 'right': head.x += 1; break;
      }
      
      // Check walls
      if (head.x < 0 || head.x >= 40 || head.y < 0 || head.y >= 20) {
        return { ...prev, gameOver: true };
      }
      
      // Check self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        return { ...prev, gameOver: true };
      }
      
      newSnake.unshift(head);
      
      // Check food
      if (head.x === prev.food.x && head.y === prev.food.y) {
        const newScore = prev.score + 10;
        const newFood = {
          x: Math.floor(Math.random() * 40),
          y: Math.floor(Math.random() * 20),
          algorithm: prev.algorithms[newScore / 10 % prev.algorithms.length]
        };
        return {
          ...prev,
          snake: newSnake,
          food: newFood,
          score: newScore,
          direction
        };
      } else {
        newSnake.pop();
        return { ...prev, snake: newSnake, direction };
      }
    });
  }, [isActive, gameState?.gameOver]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isActive) return;
      
      switch(gameType) {
        case 'snake':
          switch(e.key.toLowerCase()) {
            case 'w':
            case 'arrowup':
              e.preventDefault();
              moveSnake('up');
              break;
            case 's':
            case 'arrowdown':
              e.preventDefault();
              moveSnake('down');
              break;
            case 'a':
            case 'arrowleft':
              e.preventDefault();
              moveSnake('left');
              break;
            case 'd':
            case 'arrowright':
              e.preventDefault();
              moveSnake('right');
              break;
          }
          break;
        case 'pong':
          switch(e.key.toLowerCase()) {
            case 'w':
            case 'arrowup':
              e.preventDefault();
              setGameState(prev => ({
                ...prev,
                playerY: Math.max(0, prev.playerY - 1)
              }));
              break;
            case 's':
            case 'arrowdown':
              e.preventDefault();
              setGameState(prev => ({
                ...prev,
                playerY: Math.min(16, prev.playerY + 1)
              }));
              break;
          }
          break;
      }
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameType, isActive, moveSnake, onClose]);

  // Game loops
  useEffect(() => {
    if (!isActive || gameState?.gameOver) return;
    
    let interval;
    
    switch(gameType) {
      case 'snake':
        interval = setInterval(() => {
          moveSnake(gameState?.direction || 'right');
        }, 200);
        break;
        
      case 'pong':
        interval = setInterval(() => {
          setGameState(prev => {
            let newBallX = prev.ballX + prev.ballVelX;
            let newBallY = prev.ballY + prev.ballVelY;
            let newBallVelX = prev.ballVelX;
            let newBallVelY = prev.ballVelY;
            let newPlayerScore = prev.playerScore;
            let newAiScore = prev.aiScore;
            
            // Ball collision with top/bottom walls
            if (newBallY <= 0 || newBallY >= 19) {
              newBallVelY = -newBallVelY;
            }
            
            // Ball collision with paddles
            if (newBallX <= 2 && newBallY >= prev.playerY && newBallY <= prev.playerY + 3) {
              newBallVelX = -newBallVelX;
            }
            if (newBallX >= 37 && newBallY >= prev.aiY && newBallY <= prev.aiY + 3) {
              newBallVelX = -newBallVelX;
            }
            
            // Scoring
            if (newBallX < 0) {
              newAiScore++;
              newBallX = 20;
              newBallY = 10;
            }
            if (newBallX > 40) {
              newPlayerScore++;
              newBallX = 20;
              newBallY = 10;
            }
            
            // AI movement
            const aiTarget = newBallY - 1;
            let newAiY = prev.aiY;
            if (aiTarget > prev.aiY + 1) newAiY++;
            else if (aiTarget < prev.aiY) newAiY--;
            newAiY = Math.max(0, Math.min(16, newAiY));
            
            return {
              ...prev,
              ballX: newBallX,
              ballY: newBallY,
              ballVelX: newBallVelX,
              ballVelY: newBallVelY,
              aiY: newAiY,
              playerScore: newPlayerScore,
              aiScore: newAiScore,
              gameOver: newPlayerScore >= 5 || newAiScore >= 5
            };
          });
        }, 100);
        break;
        
      case 'memory':
        if (gameState?.phase === 'memorize') {
          const timer = setTimeout(() => {
            setGameState(prev => ({
              ...prev,
              showSequence: false,
              phase: 'recall'
            }));
          }, 3000);
          return () => clearTimeout(timer);
        }
        break;
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameType, isActive, gameState, moveSnake]);

  const handleTypingInput = (e) => {
    const value = e.target.value;
    const now = Date.now();
    
    setGameState(prev => {
      const startTime = prev.startTime || now;
      const timeElapsed = (now - startTime) / 1000 / 60; // minutes
      const wpm = value.length > 0 ? Math.round((value.length / 5) / timeElapsed) : 0;
      
      let accuracy = 100;
      if (value.length > 0) {
        const correct = value.split('').filter((char, index) => 
          char === prev.targetText[index]
        ).length;
        accuracy = Math.round((correct / value.length) * 100);
      }
      
      const gameOver = value === prev.targetText;
      
      return {
        ...prev,
        userInput: value,
        startTime,
        wpm,
        accuracy,
        gameOver
      };
    });
  };

  const handleMemoryInput = (layer) => {
    setGameState(prev => {
      const newUserSequence = [...prev.userSequence, layer];
      const gameOver = newUserSequence.length === prev.sequence.length;
      
      let score = 0;
      if (gameOver) {
        const correct = newUserSequence.filter((item, index) => 
          item === prev.sequence[index]
        ).length;
        score = Math.round((correct / prev.sequence.length) * 100);
      }
      
      return {
        ...prev,
        userSequence: newUserSequence,
        score,
        gameOver
      };
    });
  };

  const renderGame = () => {
    if (!gameState) return null;
    
    switch(gameType) {
      case 'snake':
        return (
          <div className="snake-game">
            <div className="game-board">
              {Array.from({ length: 20 }, (_, y) =>
                Array.from({ length: 40 }, (_, x) => {
                  const isSnake = gameState.snake.some(segment => 
                    segment.x === x && segment.y === y
                  );
                  const isHead = gameState.snake[0]?.x === x && gameState.snake[0]?.y === y;
                  const isFood = gameState.food.x === x && gameState.food.y === y;
                  
                  let cellClass = 'cell';
                  if (isHead) cellClass += ' snake-head';
                  else if (isSnake) cellClass += ' snake-body';
                  else if (isFood) cellClass += ' food';
                  
                  return (
                    <div key={`${x}-${y}`} className={cellClass}>
                      {isFood && '●'}
                      {isHead && '█'}
                      {isSnake && !isHead && '▓'}
                    </div>
                  );
                })
              )}
            </div>
            <div className="game-stats">
              <div>Score: {gameState.score}</div>
              <div>Next Algorithm: {gameState.food.algorithm}</div>
              {gameState.gameOver && <div className="game-over">Game Over! Press ESC to exit</div>}
            </div>
          </div>
        );
        
      case 'pong':
        return (
          <div className="pong-game">
            <div className="pong-board">
              {Array.from({ length: 20 }, (_, y) =>
                Array.from({ length: 40 }, (_, x) => {
                  const isPlayerPaddle = x <= 1 && y >= gameState.playerY && y <= gameState.playerY + 3;
                  const isAiPaddle = x >= 38 && y >= gameState.aiY && y <= gameState.aiY + 3;
                  const isBall = x === Math.round(gameState.ballX) && y === Math.round(gameState.ballY);
                  
                  let cellClass = 'cell';
                  if (isPlayerPaddle || isAiPaddle) cellClass += ' paddle';
                  else if (isBall) cellClass += ' ball';
                  
                  return (
                    <div key={`${x}-${y}`} className={cellClass}>
                      {isBall && '●'}
                      {(isPlayerPaddle || isAiPaddle) && '█'}
                    </div>
                  );
                })
              )}
            </div>
            <div className="game-stats">
              <div>Human: {gameState.playerScore} | AI: {gameState.aiScore}</div>
              {gameState.gameOver && (
                <div className="game-over">
                  {gameState.playerScore > gameState.aiScore ? 'You Win!' : 'AI Wins!'} 
                  Press ESC to exit
                </div>
              )}
            </div>
          </div>
        );
        
      case 'typing':
        return (
          <div className="typing-game">
            <div className="typing-target">
              <div>Type this code:</div>
              <div className="target-text">{gameState.targetText}</div>
            </div>
            <input
              type="text"
              value={gameState.userInput}
              onChange={handleTypingInput}
              className="typing-input"
              placeholder="Start typing..."
              autoFocus
            />
            <div className="typing-stats">
              <div>WPM: {gameState.wpm}</div>
              <div>Accuracy: {gameState.accuracy}%</div>
              {gameState.gameOver && (
                <div className="game-over">
                  Perfect! Final WPM: {gameState.wpm} | Accuracy: {gameState.accuracy}%
                </div>
              )}
            </div>
          </div>
        );
        
      case 'memory':
        return (
          <div className="memory-game">
            {gameState.phase === 'memorize' && gameState.showSequence && (
              <div className="memory-sequence">
                <div>Memorize this neural network sequence:</div>
                {gameState.sequence.map((layer, index) => (
                  <div key={index} className="sequence-item">
                    {index + 1}. {layer}
                  </div>
                ))}
              </div>
            )}
            
            {gameState.phase === 'recall' && (
              <div className="memory-recall">
                <div>Click the layers in order:</div>
                <div className="memory-options">
                  {gameState.sequence.map((layer, index) => (
                    <button
                      key={layer}
                      onClick={() => handleMemoryInput(layer)}
                      className="memory-button"
                      disabled={gameState.userSequence.includes(layer)}
                    >
                      {layer}
                    </button>
                  ))}
                </div>
                <div className="user-sequence">
                  Your sequence: {gameState.userSequence.join(' → ')}
                </div>
              </div>
            )}
            
            {gameState.gameOver && (
              <div className="game-over">
                Score: {gameState.score}% | Press ESC to exit
              </div>
            )}
          </div>
        );
        
      default:
        return <div>Game not found</div>;
    }
  };

  return (
    <div className="game-engine-overlay">
      <div className="game-window">
        <div className="game-header">
          <h3>🎮 {gameType.toUpperCase()} GAME</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        <div className="game-content">
          {renderGame()}
        </div>
        <div className="game-controls">
          <div>Controls: W/A/S/D or Arrow Keys | ESC to exit</div>
        </div>
      </div>
    </div>
  );
};

export default GameEngine;